// Port of webapp/src/components/sidebar/sidebar.tsx

import {ReactElement, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {Board} from '../../types/board'
import {BoardView} from '../../types/boardView'
import {DefaultCategory} from '../../types/category'
import {buildBoardPath} from '../../common/paths'
import {selectCategoryBoardIds, selectUncategorizedBoardIds, useSidebar} from '../../store/sidebar'
import {useBoards} from '../../store/boards'
import {useViews} from '../../store/views'
import {useTeams} from '../../store/teams'
import {useUsers} from '../../store/users'
import {Icon} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem} from '../common/menu'
import SidebarCategory from './sidebarCategory'
import SidebarBoardItem from './sidebarBoardItem'
import SidebarSettingsMenu from './sidebarSettingsMenu'
import NewBoardDialog from './newBoardDialog'

type Props = {
    activeBoardId: string
    activeViewId: string | undefined
    readOnly: boolean
}

export default function Sidebar({activeBoardId, activeViewId, readOnly}: Props): ReactElement {
    const navigate = useNavigate()
    const boardsById = useBoards((state) => state.boards)
    const views = useViews((state) => state.views)
    const categories = useSidebar((state) => state.categories)
    const isHidden = useSidebar((state) => state.isHidden)
    const toggleSidebar = useSidebar((state) => state.toggleSidebar)
    const toggleCategoryCollapsed = useSidebar((state) => state.toggleCategoryCollapsed)
    const teamId = useTeams((state) => state.activeTeamId)
    const teams = useTeams((state) => state.teams)
    const setTeamId = useTeams((state) => state.setTeamId)
    const me = useUsers((state) => state.me)

    const [creating, setCreating] = useState(false)

    const boards = useMemo(() => Object.values(boardsById).filter((b) => !b.isTemplate && b.deleteAt === 0), [boardsById])
    const viewsByBoard = useMemo(() => {
        const out: Record<string, BoardView[]> = {}
        Object.values(views).forEach((view) => {
            if (view.deleteAt === 0) {
                out[view.boardId] = [...(out[view.boardId] || []), view]
            }
        })
        Object.values(out).forEach((list) => list.sort((a, b) => a.createAt - b.createAt))
        return out
    }, [views])

    const team = teams.find((t) => t.id === teamId)

    const uncategorized = selectUncategorizedBoardIds(categories, boards.map((b) => b.id))
    const uncategorizedBoards = boards.filter((b) => uncategorized.includes(b.id))

    if (isHidden) {
        return (
            <div className='Sidebar hidden flex-none pt-4 pl-2'>
                <button type='button' className='IconButton text-sidebar-fg' onClick={() => toggleSidebar(false)} aria-label='展开侧边栏'>
                    <Icon name='menu-open' size={18}/>
                </button>
            </div>
        )
    }

    return (
        <div className='Sidebar octo-sidebar flex w-[240px] flex-none flex-col bg-sidebar-bg pt-6 pb-3 text-sidebar-fg'>
            <div className='WorkspaceTitle mb-3 flex items-center gap-2 px-4'>
                <ButtonWithMenu
                    className='min-w-0 flex-1'
                    button={
                        <span className='flex w-full cursor-pointer items-center gap-1 text-left'>
                            <span className='truncate font-bold'>{team?.title || '工作区'}</span>
                            <Icon name='chevron-down' size={12} className='shrink-0 opacity-70'/>
                        </span>
                    }
                >
                    <Menu ariaLabel='切换工作区'>
                        {teams.map((item) => (
                            <MenuItem
                                key={item.id}
                                checked={item.id === teamId}
                                icon='globe'
                                onClick={() => {
                                    setTeamId(item.id)
                                    navigate(buildBoardPath({teamId: item.id}))
                                }}
                            >
                                {item.title}
                            </MenuItem>
                        ))}
                    </Menu>
                </ButtonWithMenu>
                <button
                    type='button'
                    className='IconButton shrink-0 text-sidebar-fg opacity-70 hover:opacity-100'
                    onClick={() => toggleSidebar(true)}
                    aria-label='收起侧边栏'
                >
                    <Icon name='menu-close' size={16}/>
                </button>
            </div>

            <div className='add-board mb-3 px-2'>
                <button
                    type='button'
                    className='flex h-[30px] w-full items-center gap-2 rounded px-2 text-left text-sidebar-fg/80 hover:bg-white/10 hover:text-sidebar-fg'
                    onClick={() => setCreating(true)}
                    disabled={readOnly}
                >
                    <Icon name='add' size={14}/>
                    <span>+ 新建看板</span>
                </button>
            </div>

            <div className='flex-1 overflow-y-auto'>
                {categories.map((category) => {
                    const ids = selectCategoryBoardIds(categories, category.id)
                    const categoryBoards = ids.
                        map((id) => boards.find((b) => b.id === id)).
                        filter((b): b is Board => Boolean(b))
                    return (
                        <SidebarCategory
                            key={category.id}
                            category={category}
                            boards={categoryBoards}
                            viewsByBoard={viewsByBoard}
                            activeBoardId={activeBoardId}
                            activeViewId={activeViewId}
                            teamId={teamId}
                            readOnly={readOnly}
                            onToggleCollapsed={(categoryId) => void toggleCategoryCollapsed(teamId, categoryId)}
                        />
                    )
                })}

                {uncategorizedBoards.length > 0 && (
                    <div className='mb-1'>
                        <div className='flex h-[26px] items-center gap-1 px-2 text-xs font-semibold uppercase tracking-wide text-sidebar-fg/60'>
                            <Icon name='chevron-down' size={12}/>
                            {DefaultCategory.name}
                        </div>
                        {uncategorizedBoards.map((board) => (
                            <SidebarBoardItem
                                key={board.id}
                                board={board}
                                views={viewsByBoard[board.id] || []}
                                activeBoardId={activeBoardId}
                                activeViewId={activeViewId}
                                teamId={teamId}
                                readOnly={readOnly}
                            />
                        ))}
                    </div>
                )}

                {boards.length === 0 && (
                    <div className='px-4 text-sm text-sidebar-fg/50'>
                        还没有看板，先新建一个吧。
                    </div>
                )}
            </div>

            <div className='mt-3 flex items-center gap-2 border-t border-white/10 px-3 pt-3'>
                <span className='flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-[10px] font-semibold'>
                    {(me?.firstname || me?.username || '?').charAt(0).toUpperCase()}
                </span>
                <span className='min-w-0 flex-1 truncate text-sm' title={me?.username}>{me?.username || '未登录'}</span>
                <SidebarSettingsMenu/>
            </div>

            {creating && <NewBoardDialog onClose={() => setCreating(false)}/>}
        </div>
    )
}
